import { DEFAULT_CURRENCY } from "@/constants";
import EXCHANGE_RATES from "@/data/exchange-rates.json";

/**
 * Converts an amount 'from' a currency, 'to' another.
 * @param {number} amt - The amount to convert.
 * @param {string} from - The currency code of the incoming amount.
 * @param {string} [to] - The currency to convert to.
 * @returns {number} - The converted amount in the 'to' currency.
 */
export function convertToCurrency(amt, from, to = DEFAULT_CURRENCY) {
  const rates = EXCHANGE_RATES;
  const rateFrom = rates.find(rate => rate.code === from);
  const rateTo = rates.find(rate => rate.code === to);

  if (!rateFrom) {
    console.error(`Unknown currency, ${from}.`);
    return;
  }

  if (!rateTo) {
    console.error(`Unknown currency, ${to}.`);
    return;
  }

  const converted = (amt / rateFrom.rate) * rateTo.rate;

  return converted;
}

const formatterCache = [];

function getFormatter(currency, locale = "en-US") {
  const style = "currency";
  const maximumFractionDigits = 0;
  let formatter = formatterCache.find(c => c.id === currency);

  if (formatter) {
    return formatter.formatter;
  }

  formatter = new Intl.NumberFormat(locale, {
    style,
    currency,
    maximumFractionDigits,
  });

  formatterCache.push({
    id: currency,
    formatter,
  });

  return formatter;
}

export function getFormattedCurrencyWithConversion(
  amt,
  courseCurrency,
  preferredCurrency,
  defaultCurrency,
) {
  const courseCurrencyFormatter = getFormatter(courseCurrency);
  const originalAmtFormatted = courseCurrencyFormatter.format(amt);

  if (preferredCurrency && preferredCurrency !== courseCurrency) {
    const targetCurrencyFormatter = getFormatter(preferredCurrency);
    const convertedAmt = convertToCurrency(
      amt,
      courseCurrency,
      preferredCurrency,
    );
    const formatted = targetCurrencyFormatter.format(convertedAmt);

    return {
      converted: convertedAmt,
      formatted,
    };
  }

  if (!preferredCurrency && courseCurrency !== defaultCurrency) {
    const targetCurrencyFormatter = getFormatter(defaultCurrency);
    const convertedAmt = convertToCurrency(
      amt,
      courseCurrency,
      defaultCurrency,
    );
    const formatted = targetCurrencyFormatter.format(convertedAmt);

    return {
      converted: convertedAmt,
      formatted,
    };
  }

  return {
    converted: amt,
    formatted: originalAmtFormatted,
  };
}
